import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import type { SketchSolid, SketchFace, SketchVec3 } from './types';
import { pointKey, pointKeyOfVector, buildTopology, solidVertexPoints } from './topology';

/**
 * Whole-solid transforms (Move / Rotate / Copy).
 *
 * Pure functions over the analytic shell: origins and contour points map
 * through the matrix; normals rotate as directions and renormalize. Face
 * identities are preserved (Move/Rotate) or regenerated (Copy).
 */

const applyToTuple = (p: SketchVec3, matrix: THREE.Matrix4): SketchVec3 => {
    const v = new THREE.Vector3(...p).applyMatrix4(matrix);
    return [v.x, v.y, v.z];
};

const applyToNormal = (p: SketchVec3, matrix: THREE.Matrix4): SketchVec3 => {
    const v = new THREE.Vector3(...p).transformDirection(matrix).normalize();
    return [v.x, v.y, v.z];
};

/** Transform every face of the solid; face ids stay stable. */
export function transformSolid(solid: SketchSolid, matrix: THREE.Matrix4): SketchSolid {
    const mapFace = (face: SketchFace): SketchFace => ({
        ...face,
        origin: applyToTuple(face.origin, matrix),
        normal: applyToNormal(face.normal, matrix),
        outer: face.outer.map((p) => applyToTuple(p, matrix)),
        holes: face.holes.map((h) => h.map((p) => applyToTuple(p, matrix))),
    });
    return { ...solid, faces: solid.faces.map(mapFace) };
}

/** Deep-copy a solid with fresh ids (Copy tool; components/groups later). */
export function copySolid(solid: SketchSolid): SketchSolid {
    const mapFace = (face: SketchFace): SketchFace => ({
        ...face,
        id: uuidv4(),
        origin: [...face.origin] as SketchVec3,
        normal: [...face.normal] as SketchVec3,
        outer: face.outer.map((p) => [...p] as SketchVec3),
        holes: face.holes.map((h) => h.map((p) => [...p] as SketchVec3)),
    });
    return {
        ...solid,
        id: uuidv4(),
        faces: solid.faces.map(mapFace),
    };
}

/** Translation matrix helper. */
export function translationMatrix(delta: THREE.Vector3): THREE.Matrix4 {
    return new THREE.Matrix4().makeTranslation(delta.x, delta.y, delta.z);
}

/**
 * Subobject transform: move the selected faces INSIDE one solid (the Move
 * tool and Gumball on a face selection).
 *
 * Every original corner of a selected face lands in a weld pool keyed at
 * topology epsilon. Selected faces map through the matrix rigidly; each
 * remaining face follows ONLY the corners it shares with the pool — the
 * shared edge stays shared and the side faces stretch, exactly like moving
 * a face in a surface modeler. Fully welded bystanders (all corners shared)
 * ride along rigidly; partially welded ones re-derive their carrier plane
 * (Newell) so push/pull and shading keep working on the deformed contour.
 */
export function transformSolidFaces(solid: SketchSolid, faceIds: string[], matrix: THREE.Matrix4): SketchSolid {
    const selected = new Set(faceIds);
    const selectedFaces = solid.faces.filter((f) => selected.has(f.id));
    if (selectedFaces.length === 0) return solid;

    const weld = new Set<string>();
    for (const face of selectedFaces) {
        const collect = (loop: SketchVec3[]): void => loop.forEach((p) => weld.add(pointKey(p)));
        collect(face.outer);
        face.holes.forEach(collect);
    }

    return transformSolidByWeldPool(solid, weld, matrix);
}

/**
 * Mixed subobject transform: selected faces AND edges AND corners of one
 * solid move under ONE matrix with ONE shared weld pool (their union of
 * corners), so a face and its own border edge in the same selection move
 * their shared corners exactly once. `vertexKeys` is optional — the pure
 * face/edge callers keep their previous behavior. Pure-face, pure-edge and
 * pure-vertex callers can keep using the dedicated wrappers.
 */
export function transformSolidSubobjects(
    solid: SketchSolid,
    faceIds: string[],
    edgeKeys: string[],
    matrix: THREE.Matrix4,
    vertexKeys: string[] = []
): SketchSolid {
    const weld = new Set<string>();
    const faceSet = new Set(faceIds);
    for (const face of solid.faces) {
        if (!faceSet.has(face.id)) continue;
        const collect = (loop: SketchVec3[]): void => loop.forEach((p) => weld.add(pointKey(p)));
        collect(face.outer);
        face.holes.forEach(collect);
    }
    const topo = buildTopology(solid);
    for (const key of edgeKeys) {
        const edge = topo.edges.get(key);
        if (!edge) continue;
        weld.add(pointKeyOfVector(edge.a));
        weld.add(pointKeyOfVector(edge.b));
    }
    for (const key of vertexKeys) {
        solidVertexPoints(solid, key).forEach((p) => weld.add(pointKey(p)));
    }
    if (weld.size === 0) return solid;
    return transformSolidByWeldPool(solid, weld, matrix);
}

/**
 * Subobject transform for EDGES (Sketch edge editing): the endpoints of the
 * selected shared edges land in the weld pool. Each face then follows only
 * the pool corners it owns — the faces the edge borders move rigidly, the
 * faces touching just an endpoint stretch, and everything welded to the
 * endpoints (including loose-look collinear segments) stays connected.
 * This is the SketchUp "drag an edge, the attached faces follow" contract.
 */
export function transformSolidEdges(solid: SketchSolid, edgeKeys: string[], matrix: THREE.Matrix4): SketchSolid {
    const topo = buildTopology(solid);
    const weld = new Set<string>();
    for (const key of edgeKeys) {
        const edge = topo.edges.get(key);
        if (!edge) continue;
        weld.add(pointKeyOfVector(edge.a));
        weld.add(pointKeyOfVector(edge.b));
    }
    if (weld.size === 0) return solid;
    return transformSolidByWeldPool(solid, weld, matrix);
}

/**
 * Single-corner transform (SketchUp vertex move): exactly the given contour
 * points land in the weld pool, so the faces owning the corner stretch
 * toward its new position while everything else stays put. This is the
 * "grab a corner, the connected geometry follows" contract shared with the
 * cross-workbench junction move.
 */
export function transformSolidCorners(solid: SketchSolid, corners: SketchVec3[], matrix: THREE.Matrix4): SketchSolid {
    const weld = new Set<string>();
    for (const corner of corners) weld.add(pointKey(corner));
    if (weld.size === 0) return solid;
    return transformSolidByWeldPool(solid, weld, matrix);
}

/**
 * Where the given shared edges LAND after `matrix` (edge selections are
 * position-derived: every edit rewrites the topology keys, so callers
 * re-select through this map to keep the selection — and the gizmo — alive
 * across consecutive moves). Callers validate the returned keys against the
 * transformed solid's live topology.
 */
export function edgeKeysAfterTransform(solid: SketchSolid, edgeKeys: string[], matrix: THREE.Matrix4): string[] {
    const topo = buildTopology(solid);
    const out: string[] = [];
    for (const key of edgeKeys) {
        const edge = topo.edges.get(key);
        if (!edge) continue;
        const a = pointKeyOfVector(edge.a.clone().applyMatrix4(matrix));
        const b = pointKeyOfVector(edge.b.clone().applyMatrix4(matrix));
        out.push(a < b ? `${a}~${b}` : `${b}~${a}`);
    }
    return out;
}

/**
 * Where the given corners LAND after `matrix` (vertex selections are
 * position-derived exactly like edge keys). Callers validate the returned
 * keys against the transformed solid's live contour points.
 */
export function vertexKeysAfterTransform(solid: SketchSolid, vertexKeys: string[], matrix: THREE.Matrix4): string[] {
    const out: string[] = [];
    for (const key of vertexKeys) {
        const pts = solidVertexPoints(solid, key);
        if (pts.length === 0) continue;
        out.push(pointKeyOfVector(new THREE.Vector3(...pts[0]).applyMatrix4(matrix)));
    }
    return out;
}

/** Shared engine of the subobject transforms: map every contour point that
 *  is in the weld pool through `matrix`; untouched faces pass through,
 *  fully welded ones ride rigidly, partially welded ones re-plane. */
function transformSolidByWeldPool(solid: SketchSolid, weld: Set<string>, matrix: THREE.Matrix4): SketchSolid {
    const faces = solid.faces.map((face) => {
        let touched = 0;
        let total = 0;
        const follow = (loop: SketchVec3[]): SketchVec3[] =>
            loop.map((p) => {
                total++;
                if (weld.has(pointKey(p))) {
                    touched++;
                    return applyToTuple(p, matrix);
                }
                return p;
            });
        const outer = follow(face.outer);
        const holes = face.holes.map(follow);

        if (touched === 0) return face;
        if (touched === total) {
            return {
                ...face,
                origin: applyToTuple(face.origin, matrix),
                normal: applyToNormal(face.normal, matrix),
                outer,
                holes,
            };
        }
        return stretchedFacePlane({ ...face, outer, holes }, face.normal, matrix);
    });

    return { ...solid, faces };
}

/**
 * Best-fit carrier plane (Newell normal + loop centroid) of a stretched
 * face, orientation-checked against the original normal carried through
 * the matrix. The loop keeps its welded world points either way; only the
 * stored plane follows the deformation.
 */
function stretchedFacePlane(face: SketchFace, originalNormal: SketchVec3, matrix: THREE.Matrix4): SketchFace {
    const loop = face.outer;
    if (loop.length < 3) return face;

    let nx = 0, ny = 0, nz = 0;
    const sum = new THREE.Vector3();
    for (let i = 0; i < loop.length; i++) {
        const a = new THREE.Vector3(...loop[i]);
        const b = new THREE.Vector3(...loop[(i + 1) % loop.length]);
        nx += (a.y - b.y) * (a.z + b.z);
        ny += (a.z - b.z) * (a.x + b.x);
        nz += (a.x - b.x) * (a.y + b.y);
        sum.add(a);
    }
    const normal = new THREE.Vector3(nx, ny, nz);
    if (normal.lengthSq() < 1e-12) return face;
    normal.normalize();
    const reference = new THREE.Vector3(...originalNormal).transformDirection(matrix).normalize();
    if (normal.dot(reference) < 0) normal.negate();

    const centroid = sum.divideScalar(loop.length);
    return { ...face, origin: [centroid.x, centroid.y, centroid.z], normal: [normal.x, normal.y, normal.z] };
}

/** Rotation about an arbitrary axis through `center`. */
export function rotationMatrix(axis: THREE.Vector3, angle: number, center: THREE.Vector3): THREE.Matrix4 {
    return new THREE.Matrix4()
        .makeTranslation(center.x, center.y, center.z)
        .multiply(new THREE.Matrix4().makeRotationAxis(axis.clone().normalize(), angle))
        .multiply(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z));
}
